import { Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { IconPlus } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { Table } from "@/components/elements/Table";
import { useSelectionTable } from "@/hooks/useSelectionTable";
import { useVisible } from "@/hooks/useVisible";
import { text } from "@/theme/colors";
import { AppRoutes } from "@/routes/config";
import { DeleteProductsModal } from "@/features/organization/product/components/DeleteProductsModal";
import { RelatedActionsMenu } from "@/features/organization/product/components/RelatedActionsMenu";
import { useProductColumns } from "@/features/organization/product/hooks/useProductColumns";
import {
  deleteProductsApi,
  toggleProductActiveApi,
  useProductSettings,
} from "@/api/services/main/organization/product";
import { TProduct } from "@/api/services/main/organization/product/dtos/product.dto";
import { isProductSelectable } from "@/features/organization/product/utils/productUtils";
import { QUERY_KEYS } from "@/constants/queryKeys";
import {
  PRODUCT_ACTIVE_ERROR_MESSAGE,
  PRODUCT_ACTIVE_SUCCESS_MESSAGE,
  PRODUCT_DELETE_ERROR_MESSAGE,
  PRODUCT_DELETE_SUCCESS_MESSAGE,
} from "@/features/organization/product/constants/productEnums";
import { toast } from "@/utils/toast";

export const ProductListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { products } = useProductSettings();
  const deleteModal = useVisible(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const toggleActive = useCallback(
    async (id: number, nextActive: boolean): Promise<boolean> => {
      const snapshot = queryClient.getQueryData<TProduct[]>([QUERY_KEYS.PRODUCT.FETCH_LIST]);
      queryClient.setQueryData<TProduct[]>([QUERY_KEYS.PRODUCT.FETCH_LIST], (prev) =>
        prev?.map((p) => (p.id === id ? { ...p, isActive: nextActive } : p)),
      );

      try {
        await toggleProductActiveApi(id, { isActive: nextActive });
        toast.success(PRODUCT_ACTIVE_SUCCESS_MESSAGE);
        return true;
      } catch {
        queryClient.setQueryData([QUERY_KEYS.PRODUCT.FETCH_LIST], snapshot);
        toast.error(PRODUCT_ACTIVE_ERROR_MESSAGE);
        return false;
      }
    },
    [queryClient],
  );

  const columns = useProductColumns(toggleActive);

  const { table, rowSelectionKeys, clearSelection } = useSelectionTable<TProduct>({
    columns,
    rows: products,
    primaryKey: "id",
    // 固定商品 cannot be selected/deleted, and their checkbox is hidden entirely (not just disabled).
    enableRowSelection: (row) => isProductSelectable(row.original),
    muiSelectCheckboxProps: ({ row }) => ({
      sx: { display: isProductSelectable(row.original) ? undefined : "none" },
    }),
    onClickRow: (product) => navigate(`${AppRoutes.organizationProduct}/${product.id}`),
  });

  const selectedProducts = useMemo(
    () => products.filter((p) => rowSelectionKeys.includes(String(p.id)) && isProductSelectable(p)),
    [products, rowSelectionKeys],
  );

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    const targets = selectedProducts.filter((p) => !p.isDefault);

    let ok = false;
    try {
      await deleteProductsApi({ ids: targets.map((p) => p.id) });
      toast.success(PRODUCT_DELETE_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRODUCT.FETCH_LIST] });
      ok = true;
    } catch {
      toast.error(PRODUCT_DELETE_ERROR_MESSAGE);
    }

    setIsDeleting(false);
    deleteModal.close();
    if (ok) clearSelection();
  };

  return (
    <InnerBox showDivider={false} sx={{ borderRadius: "0 0 8px 8px" }}>
      <Stack gap={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <Stack gap={1}>
            <Typography variant="head20Bold" color={text.primary}>
              商品設定
            </Typography>
            <Typography variant="body14" color={text.secondary}>
              固定商品は削除できません。請求タイムラインは商品詳細から確認できます。
            </Typography>
          </Stack>

          <Stack direction="row" gap={1.5} flexShrink={0}>
            <Button variant="outline" startIcon={<IconPlus />} onClick={() => navigate(AppRoutes.organizationProductNew)}>
              商品を新規登録
            </Button>
            <RelatedActionsMenu disableDelete={selectedProducts.length === 0} onDelete={deleteModal.open} />
          </Stack>
        </Stack>

        <Table table={table} hiddenAddBtn />
      </Stack>

      <DeleteProductsModal
        isOpen={deleteModal.visible}
        targets={selectedProducts}
        isDeleting={isDeleting}
        onClose={deleteModal.close}
        onConfirm={handleConfirmDelete}
      />
    </InnerBox>
  );
};
